/* EP · Header — behaviour for sections/ep-header.liquid. No dependencies.
 *
 * - Desktop menus: hover intent (fine pointers) + click / keyboard; one panel open
 *   at a time; switching between open menus swaps instantly (no re-animation).
 * - Tabs (Shop): hover / click / arrow keys; a short delay while the pointer travels
 *   right toward the content, so crossing other tabs doesn't switch them.
 * - Mobile drawer: drill-down views with back, focus trap, Escape, scroll lock.
 * - Sticky modes + compact row; publishes --bh-header-offset and bh:header-offset
 *   (same contract as the old bh-header, used by sale / product sub-navs).
 */
(() => {
  if (customElements.get('ep-header')) return;

  const DESKTOP = '(min-width: 1101px)';
  const FINE = '(hover: hover) and (pointer: fine)';
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const visible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

  class EpHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.shopify-section') || this.parentElement;
      this.bar = this.querySelector('[data-eph-bar]');
      this.topEl = this.querySelector('[data-eph-top]');
      this.panelsEl = this.querySelector('[data-eph-panels]');
      this.desktop = window.matchMedia(DESKTOP);
      this.fine = window.matchMedia(FINE);
      this.ac = new AbortController();
      const sig = { signal: this.ac.signal };

      this.initSticky(sig);
      this.initPanels(sig);
      this.initTabs();
      this.initDrawer(sig);
      this.initApps(sig);
      this.initRegion(sig);
      this.initEditor(sig);
    }

    disconnectedCallback() {
      this.ac?.abort();
      this.ro?.disconnect();
      this.drawer?.remove();
      this.dscrim?.remove();
      this.io?.disconnect();
      document.documentElement.classList.remove('eph-lock');
    }

    /* ───────── sticky / compact / transparent ───────── */
    initSticky(sig) {
      const mode = this.dataset.sticky || 'compact';
      const w = this.wrapper;
      this.mode = mode;
      if (w) w.classList.add('eph-sticky--' + mode);

      const measureTop = () => {
        if (w && this.topEl) w.style.setProperty('--eph-top-h', this.topEl.offsetHeight + 'px');
      };
      measureTop();
      if ('ResizeObserver' in window && this.topEl) {
        this.ro = new ResizeObserver(() => { measureTop(); this.fit(); this.update(); });
        this.ro.observe(this.topEl);
      }

      let offset = -1;
      this.publish = () => {
        let h = Math.max(0, Math.round(this.bar.getBoundingClientRect().bottom));
        if (this.hasAttribute('data-hidden')) h = 0;
        if (h === offset) return;
        offset = h;
        document.documentElement.style.setProperty('--bh-header-offset', h + 'px');
        document.dispatchEvent(new CustomEvent('bh:header-offset', { detail: { offset: h } }));
      };

      this.atFooter = false;
      if (this.hasAttribute('data-footer-hide') && mode !== 'none' && 'IntersectionObserver' in window) {
        const footer = document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer');
        if (footer) {
          this.io = new IntersectionObserver(([entry]) => { this.atFooter = entry.isIntersecting; this.update(); });
          this.io.observe(footer);
        }
      }

      let lastY = window.scrollY;
      let hideScroll = false;
      this.update = () => {
        if (!this.isConnected) return;
        const y = window.scrollY;
        this.classList.toggle('eph--scrolled', y > 8);
        if (this.dataset.transparent === 'true') {
          this.classList.toggle('eph--transparent', y < 24 && !this.openId && !this.drawerOpen);
        }

        let stuck = false;
        if (mode === 'compact' && this.desktop.matches && w) {
          const topH = this.topEl ? this.topEl.offsetHeight : 0;
          stuck = w.getBoundingClientRect().top <= -topH + 1 && y > 0;
        }
        if (stuck !== this.hasAttribute('data-stuck')) {
          this.toggleAttribute('data-stuck', stuck);
          this.fit();
        }

        if (mode === 'scroll_up') {
          if (y > 200 && y > lastY + 8 && !this.openId) hideScroll = true;
          else if (y < lastY - 8 || y <= 200) hideScroll = false;
        }
        const footerHide = this.atFooter && y > this.bar.offsetHeight && mode !== 'none';
        const hidden = (hideScroll || footerHide) && !this.drawerOpen;
        if (hidden !== this.hasAttribute('data-hidden')) {
          if (hidden && this.openId) this.close({ instant: true });
          this.toggleAttribute('data-hidden', hidden);
          if (w) w.classList.toggle('eph-is-hidden', hidden);
          this.bar.inert = hidden;
        }
        lastY = y;
        this.publish();
      };

      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; this.update(); });
      }, { passive: true, ...sig });
      window.addEventListener('resize', () => { measureTop(); this.fit(); this.update(); if (this.openId) this.position(this.openId); }, { passive: true, ...sig });
      this.bar.addEventListener('transitionend', (e) => { if (e.target === this.bar) this.publish(); }, sig);
      document.fonts?.ready.then(() => { if (this.isConnected) { measureTop(); this.fit(); this.update(); } });
      this.update();
    }

    /* Step the menu row down until it fits (desktop). */
    fit() {
      const nav = this.querySelector('.eph-nav');
      const list = nav && nav.querySelector('.eph-nav__list');
      if (!list || !this.desktop.matches) { delete this.dataset.fit; return; }
      for (const level of ['', 'tight', 'tighter', 'tightest']) {
        if (level) this.dataset.fit = level; else delete this.dataset.fit;
        if (list.scrollWidth <= nav.clientWidth + 1) break;
      }
    }

    /* ───────── desktop panels ───────── */
    panel(id) { return this.querySelector(`[data-eph-panel="${id}"]`); }
    trigger(id) { return this.querySelector(`[data-eph-item="${id}"] [data-eph-trigger]`); }

    initPanels(sig) {
      this.openId = null;
      this.items = Array.from(this.querySelectorAll('[data-eph-item]'));
      let openT;
      let closeT;
      const cancel = () => { clearTimeout(openT); clearTimeout(closeT); };
      const scheduleClose = (ms) => { clearTimeout(openT); clearTimeout(closeT); closeT = setTimeout(() => this.close(), ms); };
      this.cancelTimers = cancel;

      this.items.forEach((item) => {
        const id = item.dataset.ephItem;
        const trig = item.querySelector('[data-eph-trigger]');
        const panel = this.panel(id);
        if (!trig || !panel) return;

        item.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse' || !this.fine.matches) return;
          cancel();
          openT = setTimeout(() => this.open(id, { hover: true }), this.openId ? 0 : 90);
        });
        item.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          scheduleClose(200);
        });
        trig.addEventListener('click', () => {
          cancel();
          /* a click right after a hover-open keeps it open instead of toggling it shut */
          if (this.openId === id && Date.now() - this.openedAt > 400) this.close();
          else this.open(id);
        });
        trig.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey && this.openId === id)) {
            e.preventDefault();
            if (this.openId !== id) this.open(id);
            requestAnimationFrame(() => this.focusables(panel)[0]?.focus());
          }
        });

        panel.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') cancel(); });
        panel.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') scheduleClose(240); });
        panel.addEventListener('keydown', (e) => {
          if (e.key !== 'Tab') return;
          const f = this.focusables(panel);
          if (!f.length) return;
          if (!e.shiftKey && document.activeElement === f[f.length - 1]) {
            e.preventDefault();
            const next = this.nextNavFocusable(item);
            this.close();
            next?.focus();
          } else if (e.shiftKey && document.activeElement === f[0]) {
            e.preventDefault();
            trig.focus();
          }
        });
      });

      /* plain links in the row close an open panel */
      this.querySelectorAll('[data-eph-link]').forEach((li) => {
        li.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse' || !this.openId) return;
          scheduleClose(120);
        });
      });

      document.addEventListener('pointerdown', (e) => {
        if (!this.openId) return;
        const inMenu = e.target.closest?.('[data-eph-item], [data-eph-panel]');
        if (!inMenu || !this.contains(inMenu)) this.close();
      }, sig);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.openId) this.close({ focus: true });
      }, sig);
      document.addEventListener('focusin', (e) => {
        if (!this.openId) return;
        const id = this.openId;
        const inside = this.panel(id)?.contains(e.target) || this.querySelector(`[data-eph-item="${id}"]`)?.contains(e.target);
        if (!inside) this.close();
      }, sig);
      this.desktop.addEventListener('change', () => { this.close({ instant: true }); this.fit(); this.update(); }, sig);
    }

    focusables(root) {
      return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => visible(el) && !el.closest('[inert]'));
    }

    nextNavFocusable(item) {
      const all = Array.from(this.querySelectorAll('.eph-nav__link'));
      const cur = item.querySelector('.eph-nav__link');
      return all[all.indexOf(cur) + 1] || null;
    }

    position(id) {
      const panel = this.panel(id);
      if (!panel || !this.panelsEl) return;
      const box = this.panelsEl.getBoundingClientRect();
      panel.style.setProperty('--eph-maxh', Math.max(240, window.innerHeight - box.top - 34) + 'px');
      /* every panel except the full-width Shop sheet opens under its own menu item,
         left edges aligned, nudged left only as far as needed to stay on screen */
      if (panel.dataset.layout !== 'tabs') {
        const t = this.trigger(id)?.getBoundingClientRect();
        if (!t) return;
        const w = panel.offsetWidth;
        const left = Math.min(Math.max(t.left - box.left, 24), box.width - w - 24);
        panel.style.left = Math.round(Math.max(24, left)) + 'px';
      }
    }

    open(id, opts = {}) {
      if (!this.desktop.matches || this.hasAttribute('data-hidden')) return;
      const panel = this.panel(id);
      if (!panel || this.openId === id) return;
      const swap = !!this.openId;
      if (this.openId) this.hidePanel(this.openId, true);

      this.openId = id;
      this.openedAt = opts.hover ? Date.now() : 0;
      this.trigger(id)?.setAttribute('aria-expanded', 'true');

      panel.hidden = false;
      panel.classList.toggle('is-swap', swap || reduced());
      this.position(id);
      panel.querySelector('[data-eph-tabs]')?._select?.(0, false, true);
      void panel.offsetWidth;
      panel.classList.add('is-open');

      this.classList.add('eph--open');
      this.update?.();
    }

    hidePanel(id, instant) {
      const panel = this.panel(id);
      this.trigger(id)?.setAttribute('aria-expanded', 'false');
      if (!panel) return;
      panel.classList.remove('is-open');
      if (instant || reduced()) {
        panel.hidden = true;
        panel.classList.remove('is-swap');
        return;
      }
      panel.classList.remove('is-swap');
      setTimeout(() => { if (this.openId !== id) panel.hidden = true; }, 180);
    }

    close({ focus = false, instant = false } = {}) {
      const id = this.openId;
      if (!id) return;
      this.cancelTimers?.();
      this.openId = null;
      this.hidePanel(id, instant);
      this.classList.remove('eph--open');
      if (focus) this.trigger(id)?.focus();
      this.update?.();
    }

    /* ───────── tabs (Shop) ───────── */
    initTabs() {
      this.querySelectorAll('[data-eph-tabs]').forEach((root) => {
        const tabs = Array.from(root.querySelectorAll('[data-eph-tab]'));
        const panes = tabs.map((t) => document.getElementById(t.getAttribute('aria-controls')));
        let current = Math.max(0, tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));

        const select = (i, focus, instant) => {
          if (i < 0 || i >= tabs.length) return;
          tabs.forEach((t, j) => {
            const on = j === i;
            t.setAttribute('aria-selected', on ? 'true' : 'false');
            t.tabIndex = on ? 0 : -1;
            const p = panes[j];
            if (!p) return;
            if (instant) p.style.transition = 'none';
            p.classList.toggle('is-active', on);
            p.inert = !on;
            if (instant) { void p.offsetWidth; p.style.transition = ''; }
          });
          current = i;
          if (focus) tabs[i].focus();
        };
        root._select = select;

        /* pointer travelling right (toward the content) gets a longer intent delay */
        let lastX = 0;
        let lastY = 0;
        let towardContent = false;
        root.addEventListener('pointermove', (e) => {
          const dx = e.clientX - lastX;
          const dy = e.clientY - lastY;
          towardContent = dx > 1 && dx >= Math.abs(dy);
          lastX = e.clientX;
          lastY = e.clientY;
        });

        let timer;
        tabs.forEach((tab, i) => {
          tab.addEventListener('pointerenter', (e) => {
            if (e.pointerType !== 'mouse') return;
            clearTimeout(timer);
            timer = setTimeout(() => { if (tab.matches(':hover')) select(i); }, towardContent ? 260 : 70);
          });
          tab.addEventListener('pointerleave', () => clearTimeout(timer));
          tab.addEventListener('click', () => select(i));
          tab.addEventListener('keydown', (e) => {
            const k = e.key;
            if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Home' || k === 'End') {
              e.preventDefault();
              const n = tabs.length;
              const next = k === 'Home' ? 0 : k === 'End' ? n - 1 : (current + (k === 'ArrowDown' ? 1 : -1) + n) % n;
              select(next, true);
            } else if (k === 'ArrowRight') {
              e.preventDefault();
              const p = panes[current];
              p && this.focusables(p)[0]?.focus();
            }
          });
        });
      });
    }

    /* ───────── mobile drawer ───────── */
    initDrawer(sig) {
      this.drawer = this.querySelector('[data-eph-drawer]');
      this.burger = this.querySelector('[data-eph-burger]');
      this.dscrim = this.querySelector('[data-eph-dscrim]');
      if (!this.drawer || !this.burger) return;
      /* the section is a z-index 80 stacking context; floating widgets sit above it,
         so the drawer + scrim live at <body> level (removed again on disconnect) */
      if (this.dscrim) document.body.appendChild(this.dscrim);
      document.body.appendChild(this.drawer);
      this.backBtn = this.drawer.querySelector('[data-eph-back]');
      this.dlogo = this.drawer.querySelector('[data-eph-dlogo]');
      this.dtitle = this.drawer.querySelector('[data-eph-dtitle]');
      this.stack = ['root'];
      this.drawerOpen = false;

      this.burger.addEventListener('click', () => this.openDrawer());
      this.drawer.querySelector('[data-eph-close]')?.addEventListener('click', () => this.closeDrawer());
      this.dscrim?.addEventListener('click', () => this.closeDrawer());
      this.drawer.addEventListener('click', (e) => {
        /* search + cart open Minimog overlays that sit below the drawer: close it first */
        if (e.target.closest('[data-eph-dclose]')) { this.closeDrawer(true); return; }
        const go = e.target.closest('[data-eph-go]');
        if (go) { this.go(go.dataset.ephGo); return; }
        if (e.target.closest('[data-eph-back]')) this.back();
      });
      this.drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          if (this.stack.length > 1) this.back(); else this.closeDrawer();
          return;
        }
        if (e.key !== 'Tab') return;
        const f = this.focusables(this.drawer);
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      this.desktop.addEventListener('change', (e) => { if (e.matches) this.closeDrawer(true); }, sig);
    }

    view(id) { return this.drawer.querySelector(`[data-eph-view="${id}"]`); }

    resetViews() {
      this.drawer.querySelectorAll('[data-eph-view]').forEach((v) => {
        const root = v.dataset.ephView === 'root';
        v.style.transition = 'none';
        v.classList.toggle('is-active', root);
        v.classList.remove('is-behind');
        v.inert = !root;
        v.scrollTop = 0;
        void v.offsetWidth;
        v.style.transition = '';
      });
      this.stack = ['root'];
      this.syncHead();
    }

    /* hims-style head: "Menu" at the top level; inside a submenu only the back
       arrow (the submenu shows its own big title) */
    syncHead() {
      const id = this.stack[this.stack.length - 1];
      const atRoot = id === 'root';
      if (this.backBtn) {
        this.backBtn.hidden = atRoot;
        this.backBtn.setAttribute('aria-label', atRoot ? 'Back' : 'Back from ' + (this.view(id)?.dataset.ephTitle || 'submenu'));
      }
      if (this.dtitle) {
        this.dtitle.textContent = this.dtitle.dataset.rootTitle || 'Menu';
        this.dtitle.hidden = !atRoot;
      }
    }

    openDrawer() {
      if (this.drawerOpen) return;
      this.close({ instant: true });
      this.resetViews();
      this.drawerOpen = true;
      this.drawer.inert = false;
      void this.drawer.offsetWidth;
      this.drawer.classList.add('is-open');
      this.dscrim?.classList.add('is-open');
      this.classList.add('eph--drawer');
      this.burger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('eph-lock');
      this.update?.();
      requestAnimationFrame(() => this.drawer.querySelector('[data-eph-close]')?.focus());
    }

    closeDrawer(instant) {
      if (!this.drawerOpen) return;
      this.drawerOpen = false;
      this.drawer.classList.remove('is-open');
      this.dscrim?.classList.remove('is-open');
      this.classList.remove('eph--drawer');
      this.burger.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('eph-lock');
      this.drawer.inert = true;
      this.update?.();
      if (!instant && visible(this.burger)) this.burger.focus();
    }

    go(id) {
      const cur = this.view(this.stack[this.stack.length - 1]);
      const next = this.view(id);
      if (!next || next === cur) return;
      next.scrollTop = 0;
      next.classList.remove('is-behind');
      cur.classList.add('is-behind');
      cur.classList.remove('is-active');
      cur.inert = true;
      next.classList.add('is-active');
      next.inert = false;
      this.stack.push(id);
      this.syncHead();
      requestAnimationFrame(() => this.backBtn?.focus());
    }

    back() {
      if (this.stack.length < 2) return;
      const curId = this.stack.pop();
      const cur = this.view(curId);
      const prev = this.view(this.stack[this.stack.length - 1]);
      cur.classList.remove('is-active', 'is-behind');
      cur.inert = true;
      prev.classList.remove('is-behind');
      prev.classList.add('is-active');
      prev.inert = false;
      this.syncHead();
      requestAnimationFrame(() => prev.querySelector(`[data-eph-go="${curId}"]`)?.focus());
    }

    /* ───────── app blocks: beside the icons on desktop, in the drawer on mobile ───────── */
    initApps(sig) {
      const home = this.querySelector('[data-eph-apps]');
      const away = this.drawer?.querySelector('[data-eph-apps-drawer]');
      if (!home || !away) return;
      const apps = Array.from(home.querySelectorAll('[data-eph-app]'));
      if (!apps.length) return;
      const place = () => {
        const target = this.desktop.matches ? home : away;
        apps.forEach((a) => { if (a.parentElement !== target) target.appendChild(a); });
      };
      place();
      this.desktop.addEventListener('change', place, sig);
    }

    /* ───────── country / currency popover ───────── */
    initRegion(sig) {
      const d = this.querySelector('[data-eph-region]');
      if (!d) return;
      document.addEventListener('pointerdown', (e) => { if (d.open && !d.contains(e.target)) d.open = false; }, sig);
      d.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && d.open) { d.open = false; d.querySelector('summary')?.focus(); }
      });
      d.addEventListener('toggle', () => { if (d.open) this.close({ instant: true }); });
    }

    /* ───────── theme editor: selecting a block opens its menu ───────── */
    initEditor(sig) {
      if (!window.Shopify || !window.Shopify.designMode) return;
      document.addEventListener('shopify:block:select', (e) => {
        const el = e.target;
        if (!el || !this.contains(el)) return;
        if (!this.desktop.matches) return;
        const item = el.closest('[data-eph-item]');
        if (item) { this.open(item.dataset.ephItem); return; }
        const panel = el.closest('[data-eph-panel]');
        if (!panel) return;
        this.open(panel.dataset.ephPanel);
        const tab = el.closest('[data-eph-tab]');
        if (tab) {
          const root = tab.closest('[data-eph-tabs]');
          const tabs = Array.from(root.querySelectorAll('[data-eph-tab]'));
          root._select?.(tabs.indexOf(tab), false, true);
        }
      }, sig);
      document.addEventListener('shopify:block:deselect', () => this.close({ instant: true }), sig);
    }
  }

  customElements.define('ep-header', EpHeader);
})();
